import React, { useEffect, useMemo, useState } from 'react';
import { paymentsApi } from '../../utils/paymentsStore';
import { CLAIM_STATUSES, STATUS_LABELS, UNITS_PER_HOUR, daysBetween, parseCents, unitsToHours } from '../../utils/claimMath';
import { Icon, fmtMoney, fmtNum, fmtDate } from '../ui';

const SERVICE_TYPES = ['Personal Care', 'Respite', 'Homemaker', 'Companion', 'Chore', 'Other'];
const PAYMENT_FIELDS = ['datePaid', 'unitsPaid', 'amountPaid', 'status'];

const blank = {
  clientId: '', patientName: '', patientId: '', payerId: '', payerName: '', serviceType: 'Personal Care',
  dosStart: '', dosEnd: '', datePaid: '', unitsBilled: '', unitsPaid: '', amountBilled: '', amountPaid: '',
  claimNumber: '', status: 'pending', statusNote: '',
};

// Labels for the review banner shown after a PDF upload; keys match the form fields.
const LABELS = {
  patientName: 'Patient name', patientId: 'Patient ID', claimNumber: 'Claim #', payerName: 'Insurance company',
  serviceType: 'Service type', dosStart: 'Service start', dosEnd: 'Service end', datePaid: 'Date paid',
  unitsBilled: 'Units billed', unitsPaid: 'Units paid', amountBilled: 'Amount billed', amountPaid: 'Amount paid',
  status: 'Status', statusNote: 'Note',
};

export default function ClaimDrawer({ claim, clients, payers, defaults, pdf, onClose, onSaved, onDeleted, showToast }) {
  const [form, setForm] = useState(blank);
  const [touched, setTouched] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Fields an uploaded PDF changed on an existing claim; the admin confirms these before saving.
  const [applied, setApplied] = useState([]);
  const isEdit = !!claim;

  useEffect(() => {
    if (claim) {
      const next = {
        ...blank, ...claim,
        unitsBilled: claim.unitsBilled || '', unitsPaid: claim.unitsPaid || '',
        amountBilled: claim.amountBilledCents ? (claim.amountBilledCents / 100).toFixed(2) : '',
        amountPaid: claim.amountPaidCents ? (claim.amountPaidCents / 100).toFixed(2) : '',
        clientId: claim.clientId || '', payerId: claim.payerId || '',
      };
      // A PDF that matched this claim (usually a remittance) fills in what the
      // claim does not have yet, and always brings the payment details, so a
      // payment can be recorded against the claim it belongs to. Billing
      // fields already on the claim are kept; the PDF reading shows as a hint.
      const changed = [];
      if (pdf && defaults) {
        for (const k of Object.keys(blank)) {
          if (k === 'clientId' || !pdf.fields?.[k] || defaults[k] == null || defaults[k] === '') continue;
          const blankNow = next[k] === '' || next[k] == null || Number(next[k]) === 0;
          const isPayment = PAYMENT_FIELDS.includes(k);
          if (!blankNow && !isPayment) continue;
          if (k === 'status' && defaults.status === 'pending') continue;
          if (String(next[k]) === String(defaults[k])) continue;
          next[k] = defaults[k];
          changed.push(k);
        }
      }
      setForm(next);
      setApplied(changed);
    } else {
      // Only known form fields come from defaults (a client deep link or a PDF draft).
      const next = { ...blank };
      for (const k of Object.keys(blank)) if (defaults?.[k] != null && defaults[k] !== '') next[k] = defaults[k];
      const c = next.clientId ? clients.find(x => x.clientId === next.clientId) : null;
      next.clientId = c ? c.clientId : '';
      if (c && !next.patientName) next.patientName = c.clientName || '';
      if (c && !next.patientId) next.patientId = c.medicaidId || '';
      setForm(next);
      setApplied([]);
    }
    setTouched(new Set()); setError(''); setConfirmDelete(false);
  }, [claim, defaults, pdf, clients]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  // Edits made by the admin clear the "confirm this" highlight on that field.
  const touch = (...keys) => setTouched(t => { const n = new Set(t); keys.forEach(k => n.add(k)); return n; });
  const edit = (k, v) => { touch(k); set(k, v); };
  const hint = (k) => (pdf && pdf.fields ? pdf.fields[k] : null);
  // On a new claim the backend says which fields to confirm; on an existing one it is whatever the PDF changed.
  const reviewKeys = isEdit ? applied : (pdf ? pdf.review : []);
  const needsReview = (k) => !!(pdf && reviewKeys.includes(k) && !touched.has(k));
  const fieldClass = (k, extra = '') => `ops-field${extra}${needsReview(k) ? ' is-review' : ''}`;
  const Hint = ({ k }) => (hint(k)?.evidence ? <span className="ops-field__hint">Seen as: {hint(k).evidence}</span> : null);
  // Units are 15 minute billing units, so 87 units reads as 21h 45m (21.75 h).
  const HoursReadout = ({ label, value, units }) => (
    <div className="ops-field"><span className="ops-label">{label}</span>
      <div className="ops-readout">{units === '' || units == null ? <span className="muted">Enter units</span> : value ? <>{value.display} <span className="muted">({fmtNum(value.decimalHours, 2)} h, units / {UNITS_PER_HOUR})</span></> : <span className="muted">Units must be a whole number</span>}</div>
    </div>
  );
  const title = pdf ? (isEdit ? 'Update claim from PDF' : 'Review claim from PDF') : isEdit ? 'Edit claim' : 'New claim';
  const onClient = (id) => {
    touch('clientId', 'patientName', 'patientId');
    const c = clients.find(x => x.clientId === id);
    setForm(f => ({ ...f, clientId: id, patientName: c ? c.clientName : f.patientName, patientId: c ? (c.medicaidId || f.patientId) : f.patientId }));
  };
  const onPayer = (id) => { touch('payerName'); const p = payers.find(x => x.payerId === id); setForm(f => ({ ...f, payerId: id, payerName: p ? p.name : f.payerName })); };

  const derived = useMemo(() => {
    const billed = parseCents(form.amountBilled), paid = parseCents(form.amountPaid);
    return {
      hoursBilled: unitsToHours(form.unitsBilled),
      hoursPaid: unitsToHours(form.unitsPaid),
      adjustment: billed - paid,
      lag: form.datePaid ? daysBetween(form.dosEnd || form.dosStart, form.datePaid) : null,
      rate: Number(form.unitsBilled) && billed ? billed / Number(form.unitsBilled) : null,
    };
  }, [form]);

  useEffect(() => {
    // Nudge status to match the money entered, but never override an explicit denial or appeal.
    // Plain edits keep the status the admin chose; a PDF applied to an existing claim nudges like a new one.
    if ((isEdit && !pdf) || form.status === 'denied' || form.status === 'appealed') return;
    const billed = parseCents(form.amountBilled), paid = parseCents(form.amountPaid);
    if (paid > 0 && paid < billed) set('status', 'partial_paid');
    else if (paid > 0 && paid >= billed) set('status', 'paid');
  }, [form.amountBilled, form.amountPaid, form.status, isEdit, pdf]);

  const validate = () => {
    if (!form.patientName.trim()) return 'Patient is required';
    if (!form.payerId) return 'Insurance company is required';
    if (!form.dosStart) return 'Service start date is required';
    if (form.dosEnd && form.dosEnd < form.dosStart) return 'Service end must be on or after the start';
    if (!(Number(form.unitsBilled) > 0)) return 'Units billed is required';
    if (!parseCents(form.amountBilled)) return 'Amount billed is required';
    if (form.datePaid && form.datePaid < form.dosStart) return 'Date paid cannot be before the service start';
    if ((form.status === 'denied' || form.status === 'appealed') && !form.statusNote.trim()) return `Add a note explaining why the claim is ${STATUS_LABELS[form.status].toLowerCase()}`;
    return '';
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate(); if (v) { setError(v); return; }
    setSaving(true); setError('');
    try {
      const saved = isEdit ? await paymentsApi.updateClaim(claim.claimId, form) : await paymentsApi.createClaim(form);
      showToast(isEdit ? 'Claim updated' : 'Claim added');
      onSaved(saved);
    } catch (err) { setError(err.message || 'Save failed'); }
    finally { setSaving(false); }
  };
  const remove = async () => {
    try { await paymentsApi.deleteClaim(claim.claimId); showToast('Claim deleted'); onDeleted(claim); }
    catch (err) { setError(err.message || 'Delete failed'); }
  };

  return (
    <>
      <div className="ops-drawer__backdrop" onClick={onClose} />
      <aside className="ops-drawer" role="dialog" aria-modal="true" aria-label={title}>
        <form onSubmit={submit} style={{ display: 'contents' }}>
          <div className="ops-drawer__head">
            <div><h2>{title}</h2>{isEdit && <div className="ops-cell-sub">{claim.claimNumber ? `Claim # ${claim.claimNumber}` : 'No claim # yet'} · {claim.patientName} · {claim.payerName} · {fmtDate(claim.dosStart)} to {fmtDate(claim.dosEnd)} · {claim.source === 'import' ? 'imported' : claim.source === 'sample' ? 'sample data' : 'entered by hand'}</div>}</div>
            <button type="button" className="ops-close" onClick={onClose} aria-label="Close"><Icon name="x" /></button>
          </div>
          <div className="ops-drawer__body">
            {error && <div className="ops-error">{error}</div>}
            {pdf && isEdit && (
              <div className="ops-note ops-note--review">
                <b>Updated from {pdf.fileName}.</b> The file was read once and was not kept.
                {applied.length > 0
                  ? <> Changed from the PDF: {applied.map(k => LABELS[k] || k).join(', ')}. Confirm these before saving.</>
                  : <> The PDF had nothing new for this claim; the existing values are shown.</>}
                {pdf.warnings.map(w => <React.Fragment key={w}> {w}</React.Fragment>)}
              </div>
            )}
            {pdf && !isEdit && (
              <div className="ops-note ops-note--review">
                <b>Filled from {pdf.fileName}.</b> The file was read once and was not kept.
                {pdf.review.length > 0 && <> Confirm these before adding: {pdf.review.map(k => LABELS[k] || k).join(', ')}.</>}
                {pdf.missing.length > 0 && <> Fields not found in the PDF are blank.</>}
                {!pdf.payerMatched && <> The insurance company was not recognised; pick it from the list or add an alias on the Payers page.</>}
                {pdf.warnings.map(w => <React.Fragment key={w}> {w}</React.Fragment>)}
              </div>
            )}

            <section className="ops-form-section">
              <h3>Service</h3>
              <div className="ops-form-grid">
                <label className="ops-field span-2">Patient (from Clients)
                  <select className="ops-select" value={form.clientId} onChange={e => onClient(e.target.value)}>
                    <option value="">Not linked to a client record</option>
                    {clients.map(c => <option key={c.clientId} value={c.clientId}>{c.clientName}{c.serviceStatus === 'service-ended' ? ' (service ended)' : ''}</option>)}
                  </select>
                </label>
                <label className={fieldClass('patientName')}>Patient name<input className="ops-input" value={form.patientName} onChange={e => edit('patientName', e.target.value)} required /><Hint k="patientName" /></label>
                <label className={fieldClass('patientId')}>Patient ID<input className="ops-input" value={form.patientId} onChange={e => edit('patientId', e.target.value)} placeholder="Medicaid or member ID" /><Hint k="patientId" /></label>
                <label className={fieldClass('payerName')}>Insurance company
                  <select className="ops-select" value={form.payerId} onChange={e => onPayer(e.target.value)} required>
                    <option value="">Select a payer</option>
                    {payers.filter(p => p.active !== false).map(p => <option key={p.payerId} value={p.payerId}>{p.name}</option>)}
                  </select>
                  <Hint k="payerName" />
                </label>
                <label className={fieldClass('serviceType')}>Service type
                  <select className="ops-select" value={form.serviceType} onChange={e => edit('serviceType', e.target.value)}>
                    {SERVICE_TYPES.map(s => <option key={s}>{s}</option>)}
                  </select>
                  <Hint k="serviceType" />
                </label>
                <label className={fieldClass('dosStart')}>Service start<input className="ops-input" type="date" value={form.dosStart} onChange={e => edit('dosStart', e.target.value)} required /><Hint k="dosStart" /></label>
                <label className={fieldClass('dosEnd')}>Service end<input className="ops-input" type="date" value={form.dosEnd} min={form.dosStart || undefined} onChange={e => edit('dosEnd', e.target.value)} /><Hint k="dosEnd" /></label>
              </div>
            </section>

            <section className="ops-form-section">
              <h3>Billing</h3>
              <div className="ops-form-grid">
                <label className={fieldClass('claimNumber')}>Claim #<input className="ops-input" value={form.claimNumber} onChange={e => edit('claimNumber', e.target.value)} placeholder="From the remittance" /><Hint k="claimNumber" /></label>
                <div className="ops-field"><span className="ops-label">Rate per unit</span><div className="ops-readout">{derived.rate != null ? fmtMoney(derived.rate) : 'Enter units and amount'}</div></div>
                <label className={fieldClass('unitsBilled')}>Units billed<input className="ops-input" type="number" min="1" step="1" value={form.unitsBilled} onChange={e => edit('unitsBilled', e.target.value)} required /><Hint k="unitsBilled" /></label>
                <label className={fieldClass('amountBilled')}>Amount billed<input className="ops-input" inputMode="decimal" value={form.amountBilled} onChange={e => edit('amountBilled', e.target.value)} placeholder="0.00" required /><Hint k="amountBilled" /></label>
                <HoursReadout label="Hours billed" value={derived.hoursBilled} units={form.unitsBilled} />
              </div>
            </section>

            <section className="ops-form-section">
              <h3>Payment</h3>
              <div className="ops-form-grid">
                <label className={fieldClass('datePaid')}>Date paid<input className="ops-input" type="date" value={form.datePaid} onChange={e => edit('datePaid', e.target.value)} /><Hint k="datePaid" /></label>
                <label className={fieldClass('status')}>Status
                  <select className="ops-select" value={form.status} onChange={e => edit('status', e.target.value)}>
                    {CLAIM_STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                  </select>
                  <Hint k="status" />
                </label>
                <label className={fieldClass('unitsPaid')}>Units paid<input className="ops-input" type="number" min="0" step="1" value={form.unitsPaid} onChange={e => edit('unitsPaid', e.target.value)} /><Hint k="unitsPaid" /></label>
                <label className={fieldClass('amountPaid')}>Amount paid<input className="ops-input" inputMode="decimal" value={form.amountPaid} onChange={e => edit('amountPaid', e.target.value)} placeholder="0.00" /><Hint k="amountPaid" /></label>
                <HoursReadout label="Hours of service paid" value={derived.hoursPaid} units={form.unitsPaid} />
                <div className="ops-field"><span className="ops-label">Adjustment</span><div className={`ops-readout${derived.adjustment > 0 ? ' neg' : ''}`}>{fmtMoney(derived.adjustment)} <span className="muted">(billed minus paid)</span></div></div>
                <div className="ops-field"><span className="ops-label">Payment lag</span><div className="ops-readout">{derived.lag != null ? `${derived.lag} days` : 'Not paid yet'}</div></div>
                {(form.status === 'denied' || form.status === 'appealed' || form.statusNote) && (
                  <label className={fieldClass('statusNote', ' span-2')}>{form.status === 'denied' ? 'Denial reason' : form.status === 'appealed' ? 'Appeal note' : 'Note'}
                    <textarea className="ops-input" rows={3} value={form.statusNote} onChange={e => edit('statusNote', e.target.value)} />
                  </label>
                )}
              </div>
            </section>
          </div>
          <div className="ops-drawer__foot">
            {isEdit ? (
              confirmDelete ? (
                <span className="ops-actions"><span className="ops-cell-sub">Delete this claim?</span><button type="button" className="ops-btn ops-btn--danger ops-btn--sm" onClick={remove}>Yes, delete</button><button type="button" className="ops-btn ops-btn--sm" onClick={() => setConfirmDelete(false)}>Cancel</button></span>
              ) : <button type="button" className="ops-btn ops-btn--ghost" onClick={() => setConfirmDelete(true)}><Icon name="trash" /> Delete</button>
            ) : <span />}
            <span className="ops-actions">
              <button type="button" className="ops-btn" onClick={onClose}>Cancel</button>
              <button type="submit" className="ops-btn ops-btn--primary" disabled={saving}>{saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add claim'}</button>
            </span>
          </div>
        </form>
      </aside>
    </>
  );
}
